import React from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { MoreMenuItemIcon, MoreMenuSection } from './more-menu-item-icon';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

interface Props {
  onClose: () => void;
  popoverOffset: number;
  sections: MoreMenuSection[];
  targetRef: React.RefObject<View | null>;
}

/**
 * Purely presentational "More" popup. Renders as a popover card next to its
 * target on desktop and medium tablet, and as a bottom sheet on small
 * layouts (BaseActionSheet decides). It knows nothing about where its items
 * come from — callers provide sections of icon + label rows.
 */
export const MoreMenuActionSheet: React.FC<Props> = ({
  onClose,
  popoverOffset,
  sections,
  targetRef,
}) => {
  return (
    <BaseActionSheet
      contentContainerStyle={styles.base}
      isVisible
      onClose={onClose}
      popoverStyle={(contentPosition, targetPosition) => ({
        left: targetPosition.width + 8,
        minWidth: 1,
        top:
          targetPosition.y +
          targetPosition.height -
          contentPosition.height -
          popoverOffset,
      })}
      popoverTarget={targetRef?.current}
    >
      {/* Named Sprout Kit icons are Tamagui components and need a
          SproutKitProvider ancestor — now mounted once at the app root
          (expo-app.tsx), so this popup no longer provides its own. */}
      <View style={styles.listContainer}>
        {sections.map((section) => (
          <View key={section.title ?? section.items[0]?.id ?? 'untitled'}>
            {section.title ? (
              <Text
                numberOfLines={1}
                style={[
                  TYPOGRAPHY.xsmall,
                  FONTS.primaryBold,
                  styles.sectionTitle,
                ]}
              >
                {section.title}
              </Text>
            ) : null}
            {section.items.map((item) => (
              <TouchableOpacity
                key={item.id}
                onPress={() => {
                  item.onPress();
                  onClose();
                }}
                style={styles.listItem}
                testID={`more-menu-item-${item.id}`}
              >
                <MoreMenuItemIcon item={item} />
                <Text
                  numberOfLines={1}
                  style={[
                    TYPOGRAPHY.small,
                    FONTS.primaryBold,
                    styles.itemLabel,
                  ]}
                >
                  {item.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        ))}
      </View>
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  base: {
    paddingHorizontal: 0,
    paddingVertical: 8,
  },
  itemLabel: {
    marginLeft: 8,
  },
  listContainer: {
    maxWidth: 260,
    minWidth: 168,
  },
  listItem: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  sectionTitle: {
    paddingBottom: 4,
    paddingHorizontal: 16,
    paddingTop: 8,
  },
});
